import React, { useState, useEffect, useRef } from "react";
import type * as controls from "../PhoenixFormsV4.Controls";
import { usePhoenixForms } from "../PhoenixFormsProvider";
import PhoenixSelect1 from "./PhoenixSelect1";
import PhoenixInput from "./PhoenixInput";
import PhoenixTextArea from "./PhoenixTextArea";
import PhoenixOutput from "./PhoenixOutput";
import PhoenixSelect from "./PhoenixSelect";
import PhoenixSelectGroup from "./PhoenixSelectGroup";
import PhoenixSelectGroupItem from "./PhoenixSelectGroupItem";
import PhoenixRepeat from "./PhoenixRepeat";
import PhoenixRepeatObject from "./PhoenixRepeatObject";
import PhoenixGroup from "./PhoenixGroup";
import PhoenixPhoto from "./PhoenixPhoto";
import PhoenixPhotoTagGroup from "./PhoenixPhotoTagGroup";
import PhoenixRulesetDisplay from "./PhoenixRulesetDisplay";
import PhoenixRecommendationsetDisplay from "./PhoenixRecommendationsetDisplay";
import PhoenixFormsV4ReadOnlyControl from "./PhoenixFormsV4ReadOnlyControl";
import { getVerticalScrollParent, scrollToElementVertical, cssAnimation } from "tools/dom";
import PhoenixFileControl from "./PhoenixFileControl";

interface Props {
  controlProps: controls.Control;
  parentControlProps?: controls.Control;
  parentOnBlur?: () => void;
  onControlJump?: () => void;
};

/**HOC and factory for all phoenix forms control types */
const PhoenixFormsV4Control = (
  {
    controlProps,
    parentControlProps,
    parentOnBlur,
    onControlJump,
  }: Props
) => {  
  console.log("PhoenixFormsV4Control Render");  

  const [controlState, setControlState] = useState<controls.Control>(controlProps);
  const { getProxy, addControlRef, removeControlRef, setControlValue, getWebRuntimeAppData, validationErrors } = usePhoenixForms();
  const [showValidations, setShowValidations] = useState<boolean>(false);
  const htmlElement = useRef<HTMLDivElement>();
  
  const jumpToControl = () => {
    if (!htmlElement.current) return;

    onControlJump?.(); // this is used to uncollapse parent controls
    setTimeout(() => {
      /*
      onControlJump can trigger a render cycle for the parent controls, so we have to wait for the rendering to be completed
       before we can do anything requiring calculated window positions. I tried to do this in an effect (my setting a state called jump to current date and adding an effect on jump)
       but the vertical scroll was not working at all, only the animation. This seems to work consistently.
      */
      const parent = getVerticalScrollParent(htmlElement.current);
      scrollToElementVertical(parent, htmlElement.current, -46 /* we have a display "sticky" header that is not computed in the scroll container! */);
      cssAnimation(htmlElement.current, "phoenix-red-flash");
    }, 0);    
  };

  const { id, type, parentId } = controlState;  
  addControlRef(id, { type, parentId, setControlState, jumpToControl }); // this needs to happen on every render

  useEffect(() => {    
    return () => { removeControlRef(id); };
  }, []);

  useEffect(() => {
    if (validationErrors !== null) // if we have run at least 1 global validation
      setShowValidations(true);
  }, [validationErrors]);

  if (!controlState.visible) return null;
  if (controlState.readonly) return <PhoenixFormsV4ReadOnlyControl controlProps={controlState} parentControlProps={parentControlProps} />;

  // utility functions
  const onBlur = () => {
    if (!showValidations)
      setShowValidations(true);
  };  

  // proxy wrapper functions
  const updateValue = (newValue) => {
    // hacking around awkward interaction between react and forms library
    //  call a proxy wrapper that updates control state, reducer state, then calls the actual proxy method
    setControlValue(id, newValue);
  };
  const addInstance = () => {
    getProxy().repeatAdd(id);
  };
  const deleteInstance = () => {
    const repeatObject = controlState as controls.RepeatObject;
    getProxy().repeatDelete(repeatObject.parentId, repeatObject.repeatIndex);
  };

  const controlFactory = () => {
    switch (controlState.type) {
      case "input":
        return <PhoenixInput controlProps={controlState as controls.FormControl} updateValue={updateValue} onControlBlur={onBlur} />;
      case "textarea":
        return <PhoenixTextArea controlProps={controlState as controls.FormControl} updateValue={updateValue} onControlBlur={onBlur} />;
      case "output":
        return <PhoenixOutput controlProps={controlState as controls.OutputControl} />;
      case "select":
        return <PhoenixSelect controlProps={controlState as controls.ListFormControl} updateValue={updateValue} onControlBlur={onBlur} />;
      case "select1":
        return <PhoenixSelect1 controlProps={controlState as controls.ListFormControl} updateValue={updateValue} onControlBlur={onBlur} />;
      case "selectgroup":
        return <PhoenixSelectGroup controlProps={controlState as controls.ContainerControl} onControlBlur={onBlur} />;
      case "selectgroupitem":
        return <PhoenixSelectGroupItem controlProps={controlState as controls.SelectGroupItem} updateValue={updateValue} parentOnBlur={parentOnBlur} />;
      case "repeat":
        return <PhoenixRepeat controlProps={controlState as controls.RepeatControl} addInstance={addInstance} parentControlJump={onControlJump ?? (() => {})} />;
      case "repeatobject":
        return <PhoenixRepeatObject controlProps={controlState as controls.RepeatObject} parentControlProps={parentControlProps as controls.RepeatControl} deleteInstance={deleteInstance} parentControlJump={onControlJump ?? (() => {})} />;
      case "group":
        return <PhoenixGroup controlProps={controlState as controls.ContainerControl} />;
      case "photo":
        return <PhoenixPhoto controlProps={controlState as controls.ContainerControl} proxy={getProxy()} webRuntimeData={getWebRuntimeAppData()} />;
      case "phototaggroup":
        return <PhoenixPhotoTagGroup controlProps={controlState as controls.PhotoTagGroup} />;
      case "rulesetdisplay":
        return <PhoenixRulesetDisplay controlProps={controlState as controls.RulesetDisplay} />;
      case "recommendationsetdisplay":
        return <PhoenixRecommendationsetDisplay controlProps={controlState as controls.RecommendationsetDisplay} />;
      case "file":
        return <PhoenixFileControl controlProps={controlState as controls.FormControl} proxy={getProxy()} webRuntimeData={getWebRuntimeAppData()} />;
      default:
        return <div>InvalidControl</div>;
    }
  }

  const validationMessages = () => {
    // NOTE: photo + file controls are exceptions to this and handle their own validation messages
    if (controlState.type === "photo") return <></>;
    if (controlState.type === "file") return <></>;

    const controlStateFormControl = controlState as controls.FormControl;
    const validationMessage = controlStateFormControl.alert ?? "Invalid";

    return <div className="validation-messages">
      {showValidations && controlState.required && !controlState.value &&
        <div className="phoenix-validation">{"[ * Required ]"}</div>
      }
      {showValidations && !controlState.valid &&
        <div className="phoenix-validation">{`[ * ${validationMessage} ]`}</div>
      }
    </div>;
  }

  const className = `formcontrol ${controlState.type}`;

  return (
    <div className={className} ref={htmlElement}>
      {controlFactory()}
      {validationMessages()}
    </div>
  );
};

export default PhoenixFormsV4Control;